import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Modal from "@mui/material/Modal";
import { FiGift, FiPercent } from "react-icons/fi";
import { IoCloseOutline } from "react-icons/io5";
import {
  MdOutlineContentCopy,
  MdCheck,
  MdOutlineEmail,
  MdOutlineShare,
} from "react-icons/md";
import { FaWhatsapp, FaXTwitter, FaLinkedinIn, FaFacebookF } from "react-icons/fa6";
import { LuLink, LuUserPlus, LuBadgeDollarSign } from "react-icons/lu";

import BoltButton from "../../../UI/Buttons/BoltButton";
import Loader from "../../../CircularLoader/CircularLoader";
import { showSnackBar } from "../../../../store/common/snackBarSlice";
import {
  fetchReferralStatistics,
  createReferral,
} from "../../../../services/referral.services";
import { formatMarkup, pickPromoReferral } from "../../../../helpers/referral";

// Marketing defaults — the user is never asked for these. Reward rate is editable
// (percentage only for now); the user cap is hidden and always sent as this value.
const DEFAULT_MARKUP = 5;
const DEFAULT_MAX_USERS = 100000;

// This project's tailwind.config overrides `amber` to a flat value and has no
// `orange` scale, so numbered classes like `amber-500`/`orange-500` don't exist.
// Brand colors are therefore hard-coded as hex here.
const C = {
  amber: "#F59E0B",
  amberDark: "#D97706",
  amber100: "#FEF3C7",
  amber300: "#FCD34D",
  orange: "#EA580C",
};
const GRADIENT = `linear-gradient(135deg, ${C.amber} 0%, ${C.orange} 100%)`;
const GRADIENT_H = `linear-gradient(to right, ${C.amber} 0%, ${C.orange} 100%)`;

const buildReferralLink = (code) =>
  `${window.location.origin}/register/shipper?ref=${encodeURIComponent(code)}`;

const HowItWorksStep = ({ icon: Icon, title, text, step }) => (
  <div className="flex items-start gap-3">
    <div className="relative flex-shrink-0">
      <div
        className="flex h-9 w-9 items-center justify-center rounded-full"
        style={{ backgroundColor: C.amber100, color: C.amberDark }}
      >
        <Icon className="h-[18px] w-[18px]" />
      </div>
      <span
        className="absolute -top-1 -left-1 flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold text-white"
        style={{ backgroundColor: C.amber }}
      >
        {step}
      </span>
    </div>
    <div>
      <p className="m-0 text-sm font-semibold text-ui-text">{title}</p>
      <p className="m-0 text-xs leading-snug text-ui-text-muted">{text}</p>
    </div>
  </div>
);

const ShareButton = ({ icon: Icon, label, onClick, color }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    onClick={onClick}
    style={{ backgroundColor: color }}
    className="flex h-9 w-9 items-center justify-center rounded-full text-white transition-transform hover:scale-110"
  >
    <Icon className="h-[18px] w-[18px]" />
  </button>
);

export default function ReferralPromo() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation("nav");
  const SHARE_MESSAGE = t("referral.shareMessage");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [referral, setReferral] = useState(null);
  const [markup, setMarkup] = useState(String(DEFAULT_MARKUP));
  const [copied, setCopied] = useState(false);

  const notify = (text, severity) =>
    dispatch(showSnackBar({ text, severity }));

  const code = referral?.code ?? referral?.referral_code ?? null;
  const link = code ? buildReferralLink(code) : "";

  // Manage Referrals opens its Create form when it receives this state.
  const goToCustomReferral = () => {
    setOpen(false);
    navigate("/settings/referrals", { state: { openCreate: true } });
  };

  const loadReferral = async () => {
    setLoading(true);
    setLoadError(false);
    // The statistics endpoint returns all of the owner's referrals, unpaginated,
    // so the default one is found even when there are many custom ones.
    const res = await fetchReferralStatistics();
    setLoading(false);
    if (res?.error) {
      // Show a retry instead of "Get my referral link", which would create a
      // duplicate referral for an owner who already has one.
      setLoadError(true);
      notify(
        res?.error?.response?.data?.message ?? t("referral.loadError"),
        "error"
      );
      return;
    }
    setReferral(pickPromoReferral(res?.data?.result?.referrals ?? []));
  };

  useEffect(() => {
    if (open) {
      setCopied(false);
      loadReferral();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleCreate = async () => {
    const rate = Number(markup);
    if (markup === "" || isNaN(rate) || rate < 0) {
      notify(t("referral.invalidRate"), "error");
      return;
    }
    setSaving(true);
    try {
      const res = await createReferral({
        markup: rate,
        markup_type: "%",
        max_users: DEFAULT_MAX_USERS,
      });
      if (res?.error) {
        notify(
          res?.error?.response?.data?.message ?? t("referral.createError"),
          "error"
        );
        return;
      }
      setReferral(res?.data?.result ?? null);
      notify(res?.data?.message ?? t("referral.ready"), "success");
    } catch (error) {
      notify(
        error?.response?.data?.message ?? t("referral.createError"),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      notify(t("referral.copied"), "success");
    } catch {
      notify(t("referral.copyFailed"), "error");
    }
  };

  const shareVia = (platform) => {
    const text = `${SHARE_MESSAGE} ${link}`;
    const encodedText = encodeURIComponent(text);
    const encodedUrl = encodeURIComponent(link);
    const map = {
      whatsapp: `https://wa.me/?text=${encodedText}`,
      email: `mailto:?subject=${encodeURIComponent(
        t("referral.shareSubject")
      )}&body=${encodedText}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        SHARE_MESSAGE
      )}&url=${encodedUrl}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    };
    window.open(map[platform], "_blank", "noopener,noreferrer");
  };

  return (
    <>
      {/* Prominent navbar CTA with a decent, tasteful pulse */}
      {/* Bootstrap's !important .shadow-sm and .opacity-75 would block the
          hover shadow and the ping fade, hence the arbitrary values. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={t("referral.title")}
        aria-label={t("referral.title")}
        style={{ backgroundImage: GRADIENT_H }}
        className="relative flex items-center gap-1.5 rounded-full px-1.5 sm:px-2.5 xl:px-1.5 2xl:px-2.5 py-1.5 text-[13px] font-semibold text-white shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] transition-all duration-200 hover:shadow-md hover:brightness-105"
      >
        {/* pulsing dot */}
        <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
          <span
            className="absolute inline-flex h-full w-full motion-safe:animate-ping rounded-full opacity-[.75]"
            style={{ backgroundColor: C.amber300 }}
          />
          <span
            className="relative inline-flex h-2 w-2 rounded-full ring-1 ring-white"
            style={{ backgroundColor: C.amber }}
          />
        </span>
        <FiGift className="h-4 w-4 motion-safe:animate-pulse text-white" />
        {/* Icon only (a 28px circle) on phones and from 1280 to 1535px, where
            the owner's full menu leaves no room for the label. */}
        <span className="hidden sm:inline xl:hidden 2xl:inline text-white">
          {t("referral.title")}
        </span>
      </button>

      <Modal open={open} onClose={() => setOpen(false)}>
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="refer-earn-title"
          className="absolute top-1/2 left-1/2 flex max-h-[92vh] w-[520px] max-w-[95vw] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl bg-ui-surface shadow-2xl outline-none"
        >
          {/* Hero */}
          <div
            className="relative px-6 py-4 text-white"
            style={{ backgroundImage: GRADIENT }}
          >
            <button
              type="button"
              aria-label={t("referral.close")}
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 text-white/80 transition-colors hover:text-white"
            >
              <IoCloseOutline size={24} />
            </button>
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/20 backdrop-blur">
                <FiGift className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2
                  id="refer-earn-title"
                  className="m-0 text-xl font-bold leading-tight text-white"
                >
                  {t("referral.title")}
                </h2>
                <p className="m-0 text-sm text-white/90">
                  {t("referral.intro")}
                </p>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            {/* How it works */}
            <div className="flex flex-col gap-3">
              <HowItWorksStep
                step={1}
                icon={LuLink}
                title={t("referral.step1Title")}
                text={t("referral.step1Text")}
              />
              <HowItWorksStep
                step={2}
                icon={LuUserPlus}
                title={t("referral.step2Title")}
                text={t("referral.step2Text")}
              />
              <HowItWorksStep
                step={3}
                icon={LuBadgeDollarSign}
                title={t("referral.step3Title")}
                text={t("referral.step3Text")}
              />
            </div>

            <div className="my-3.5 h-px bg-ui-border" />

            {loading ? (
              <div className="flex justify-center py-6">
                <Loader />
              </div>
            ) : loadError ? (
              <div className="flex flex-col items-center gap-[12px] py-[16px] text-center">
                <p className="m-0 text-sm text-ui-text-muted">
                  {t("referral.loadFailed")}
                </p>
                <BoltButton
                  label={t("referral.tryAgain")}
                  variant="outline"
                  size="sm"
                  onClick={loadReferral}
                />
              </div>
            ) : link ? (
              <>
                {/* Referral link */}
                <label
                  htmlFor="referral-link"
                  className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ui-text-subtle"
                >
                  {t("referral.yourLink")}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    id="referral-link"
                    readOnly
                    value={link}
                    onFocus={(e) => e.target.select()}
                    className="h-11 flex-1 rounded-lg border-[1px] border-ui-border bg-ui-surface-sunken px-3 text-sm text-ui-text outline-none"
                  />
                  <button
                    type="button"
                    onClick={copyLink}
                    style={{
                      backgroundColor: copied ? "#10B981" : C.amber,
                    }}
                    className="flex h-11 items-center gap-1.5 rounded-lg px-4 text-sm font-semibold text-white transition-colors hover:brightness-105"
                  >
                    {copied ? (
                      <MdCheck size={18} />
                    ) : (
                      <MdOutlineContentCopy size={16} />
                    )}
                    {copied ? t("referral.copiedShort") : t("referral.copy")}
                  </button>
                </div>

                {/* Social share */}
                <div className="mt-3">
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-ui-text-subtle">
                    <MdOutlineShare size={14} /> {t("referral.shareVia")}
                  </p>
                  <div className="flex items-center gap-3">
                    <ShareButton
                      icon={FaWhatsapp}
                      label={t("referral.shareWhatsapp")}
                      onClick={() => shareVia("whatsapp")}
                      color="#25D366"
                    />
                    <ShareButton
                      icon={MdOutlineEmail}
                      label={t("referral.shareEmail")}
                      onClick={() => shareVia("email")}
                      color="#6B7280"
                    />
                    <ShareButton
                      icon={FaXTwitter}
                      label={t("referral.shareX")}
                      onClick={() => shareVia("twitter")}
                      color="#000000"
                    />
                    <ShareButton
                      icon={FaLinkedinIn}
                      label={t("referral.shareLinkedin")}
                      onClick={() => shareVia("linkedin")}
                      color="#0A66C2"
                    />
                    <ShareButton
                      icon={FaFacebookF}
                      label={t("referral.shareFacebook")}
                      onClick={() => shareVia("facebook")}
                      color="#1877F2"
                    />
                  </div>
                </div>

                {/* Reward rate */}
                <div className="mt-3.5 flex items-center justify-between rounded-lg px-4 py-2.5 bg-state-warning-bg">
                  <div>
                    <p className="m-0 text-xs font-medium text-state-warning-text">
                      {t("referral.yourRate")}
                    </p>
                    <p className="m-0 text-lg font-bold text-state-warning-text">
                      {formatMarkup(
                        referral?.markup ?? DEFAULT_MARKUP,
                        referral?.markup_type
                      )}
                    </p>
                  </div>
                </div>

                {/* Custom rate */}
                <div className="mt-3 flex flex-col items-start gap-[8px] rounded-lg border-[1px] border-ui-border px-[12px] py-[10px] sm:flex-row sm:items-center sm:justify-between">
                  <p className="m-0 text-xs leading-snug text-ui-text-muted">
                    {t("referral.customRateHint")}
                  </p>
                  <BoltButton
                    icon={FiPercent}
                    label={t("referral.createCustom")}
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    onClick={goToCustomReferral}
                  />
                </div>
              </>
            ) : (
              /* No referral yet — one-click generate with an editable rate */
              <div>
                <div className="mb-4 flex items-center justify-between rounded-lg px-4 py-3 bg-state-warning-bg">
                  <div>
                    <p className="m-0 text-xs font-medium text-state-warning-text">
                      {t("referral.yourRate")}
                    </p>
                    <p className="m-0 text-[11px] text-ui-text-muted">
                      {t("referral.rateHint")}
                    </p>
                  </div>
                  <div className="relative">
                    <input
                      type="number"
                      aria-label={t("referral.rateAria")}
                      value={markup}
                      onChange={(e) => setMarkup(e.target.value)}
                      className="h-9 w-24 rounded-md border-[1px] border-state-warning-border pl-2 pr-6 text-sm text-ui-text outline-none focus:border-brand focus:ring-1 focus:ring-brand"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-ui-text-subtle">
                      %
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={saving}
                  style={{ backgroundImage: GRADIENT_H }}
                  className="flex h-11 w-full items-center justify-center gap-2 rounded-lg text-sm font-semibold text-white transition-all hover:brightness-105 disabled:opacity-60"
                >
                  {saving ? <Loader /> : <FiGift className="h-4 w-4 text-white" />}
                  {saving ? t("referral.generating") : t("referral.getLink")}
                </button>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}
